import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Row, Col, Form, Alert } from 'react-bootstrap';
import LoadingSpinner from '../LoadingSpinner';
import { QuestionSquareFill } from 'react-bootstrap-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PROPERTY_ID } from '../../apiCalls';
import { useAddressLookup } from '../../hooks/useAddressLookup';
import { useEmailQuote } from '../../hooks/useEmailQuote';
import { markJourneyInteraction, noteJourneyField, trackJourney } from '../../utils/journey';
import {
    ResultsModalHeading,
    ResultsModalBody,
    ModalIconRow,
    AddressButton,
    PdfButton,
    ModalButton,
    ResultsModalHero,
    ResultsModalSection,
    ResultsModalMetricCard,
    ResultsModalDetailsSection,
    ResultsModalDetailsHeading,
    ResultsModalDetailsGrid,
    ResultsModalDetailItem,
    ResultsModalDetailsNote,
    ResultsModalContainer,
    ResultsModalComparisonGrid,
    ResultsModalComparisonCard,
    ResultsModalComparisonLabel,
    ResultsModalComparisonValue,
    ResultsModalSelectedLabel,
    ResultsModalContactForm,
    ResultsModalFormBlock,
    ResultsModalFormLegend,
    ResultsModalFormHint,
    ResultsModalFormActions,
    ResultsModalPrivacyNote,
} from '../../styles/ComponentStyles';

const round = (value, decimals) => {
    if (value == null) return null;
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
};

const ResultRow = ({ label, value }) => (
    <ResultsModalDetailItem>
        <dt>{label}</dt>
        <dd>{value}</dd>
    </ResultsModalDetailItem>
);

const ResultsModal = ({
    resultsModalIsOpen,
    closeResultsModal,
    results,
    openAssumptionsModal,
    formValues,
    onSelectPanelOption,
    panelComparisons = {},
    panelComparisonCounts = [],
    selectedPanelCount,
    isLoadingPanelComparisons = false,
    panelComparisonError = '',
}) => {
    const postCode = formValues?.postcode || '';

    const schema = useMemo(() => Yup.object().shape({
        firstName: Yup.string().required('Required'),
        lastName: Yup.string().required('Required'),
        address1: Yup.string().required('Required'),
        address2: Yup.string().required('Required'),
        address3: Yup.string(),
        postCode: Yup.string().required('Required'),
        phone: Yup.string()
            .matches(/^[\d\s()+-]{7,15}$/, 'Invalid phone number')
            .required('Required'),
        email: Yup.string().email().required('Required'),
    }), []);

    const {
        calculationData = {},
        calculatedValue,
        usedEnergyMCS,
        exportedEnergyMCS,
        usedEnergyPercentageMCS,
        usedEnergyPercent,
        exportedEnergyPercent,
        usedEnergyPercentagePercent,
        installationCost,
        usageIncome,
        exportIncome,
        costSaving,
        yearsForPayback,
        simplePaybackYears,
        breakEvenYear,
        selfConsumptionPercentage,
        annualConsumptionKwh,
    } = results;

    const usedEnergyValue = usedEnergyMCS ?? usedEnergyPercent;
    const exportedEnergyValue = exportedEnergyMCS ?? exportedEnergyPercent;
    const usedEnergyPercentValue = usedEnergyPercentageMCS ?? usedEnergyPercentagePercent;
    const displayedBreakEvenYear = breakEvenYear ?? yearsForPayback;
    const displayedSimplePayback = simplePaybackYears ?? (installationCost > 0 && costSaving > 0 ? round(installationCost / costSaving, 1) : null);
    const displayedSelfConsumption = selfConsumptionPercentage ?? usedEnergyPercentValue;
    const displayedAnnualConsumption = annualConsumptionKwh ?? results.avgAnnualConsumption ?? formValues?.annualConsumptionKwh;

    const { co2UK, currency = 'GBP' } = calculationData;
    const currencySymbol = currency === 'EUR' ? '\u20ac' : '\u00a3';
    const co2Saving = co2UK != null && calculatedValue != null
        ? round(calculatedValue * co2UK, 1)
        : null;

    const displayedResults = {
        usageIncome,
        exportIncome,
        costSaving,
        installationCost,
        yearsForPayback,
        simplePaybackYears: displayedSimplePayback,
        breakEvenYear: displayedBreakEvenYear,
        selfConsumptionPercentage: displayedSelfConsumption,
        annualConsumptionKwh: displayedAnnualConsumption,
        co2Saving,
    };

    const [selectedAddressId, setSelectedAddressId] = useState('');
    const {
        addressOptions,
        isLoading: isAddressLoading,
        isLookingUp,
        isFetchingAddress,
        error: addressError,
        lookupAddresses,
        fetchAddress,
    } = useAddressLookup(postCode);
    const { sendResponse, isSending, sendQuote } = useEmailQuote();

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            postCode: postCode,
            address1: '',
            address2: '',
            address3: '',
            phone: '',
            email: '',
        },
        enableReinitialize: true,
        onSubmit: () => {
            const quotePanelCount = Number(selectedPanelCount ?? formValues?.size);
            const payload = {
                values: formik.values,
                formValues: {
                    ...formValues,
                    size: quotePanelCount,
                },
                results,
                displayedResults,
                property: PROPERTY_ID,
            };
            sendQuote(payload);
        },
        validationSchema: schema,
        validateOnBlur: false,
        validateOnChange: false,
    });

    // Populate form when an address is selected from the dropdown
    useEffect(() => {
        if (!selectedAddressId) return;
        fetchAddress(selectedAddressId).then(address => {
            if (address) {
                formik.setValues({
                    ...formik.values,
                    postCode: address.postCode,
                    address1: address.address1,
                    address2: address.address2,
                    address3: address.address3,
                });
            }
        });
        setSelectedAddressId('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAddressId]);

    const isLoading = isAddressLoading || isSending;

    useEffect(() => {
        if (resultsModalIsOpen) {
            trackJourney('lead_form_opened');
        }
    }, [resultsModalIsOpen]);

    return (
        <ResultsModalContainer
            show={resultsModalIsOpen}
            onHide={closeResultsModal}
            backdrop='static'
            size='xl'
            centered
            scrollable
            animation={false}
        >
            <ModalIconRow>
                <p>Info</p>
                <div role="button" tabIndex={0} onClick={openAssumptionsModal} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAssumptionsModal(); } }}>
                    <QuestionSquareFill />
                </div>
            </ModalIconRow>
            <ResultsModalHeading>Your results</ResultsModalHeading>

            <ResultsModalBody>
                <ResultsModalHero>
                    <p style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-color)' }}>Projected performance</p>
                    <p style={{ marginBottom: '0.75rem', fontSize: '1.05rem', color: 'var(--muted-text-color)' }}>
                        This estimate is based on the details you entered and should help you compare likely annual generation and savings.
                    </p>
                    <Row className='g-3'>
                        <Col xs={4} md={4}>
                            <ResultsModalMetricCard>
                                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted-text-color)' }}>Generation</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-color)' }}>{round(calculatedValue, 0)} kWh/yr</div>
                            </ResultsModalMetricCard>
                        </Col>
                        <Col xs={4} md={4}>
                            <ResultsModalMetricCard>
                                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted-text-color)' }}>Annual saving</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-color)' }}>{currencySymbol}{round(costSaving, 2)}</div>
                            </ResultsModalMetricCard>
                        </Col>
                        <Col xs={4} md={4}>
                            <ResultsModalMetricCard>
                                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--muted-text-color)' }}>Simple payback</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-color)' }}>{displayedSimplePayback != null ? `${displayedSimplePayback} yrs` : '—'}</div>
                            </ResultsModalMetricCard>
                        </Col>
                    </Row>
                </ResultsModalHero>

                {typeof onSelectPanelOption === 'function' && panelComparisonCounts.length > 0 && (
                    <ResultsModalSection>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-color)' }}>Compare system sizes</div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--muted-text-color)', margin: '0.25rem 0 0.75rem' }}>Choose the estimate you want us to use for your quote.</div>
                        <ResultsModalComparisonGrid>
                            {panelComparisonCounts.map((panelCount) => {
                                const comparison = panelComparisons[panelCount];
                                const isSelected = Number(selectedPanelCount) === panelCount;
                                const comparisonSimplePayback = comparison?.simplePaybackYears
                                    ?? (comparison?.installationCost > 0 && comparison?.costSaving > 0
                                        ? round(comparison.installationCost / comparison.costSaving, 1)
                                        : null);
                                const comparisonBreakEvenYear = comparison?.breakEvenYear ?? comparison?.yearsForPayback;

                                return (
                                    <ResultsModalComparisonCard
                                        key={panelCount}
                                        type='button'
                                        data-cy={`panel-comparison-${panelCount}`}
                                        $selected={isSelected}
                                        aria-pressed={isSelected}
                                        onClick={() => onSelectPanelOption(panelCount)}
                                        disabled={!comparison}
                                    >
                                        <ResultsModalComparisonLabel>{panelCount} panels</ResultsModalComparisonLabel>
                                        {comparison ? (
                                            <>
                                                <ResultsModalComparisonValue>
                                                    <span>Generation</span>
                                                    <strong>{round(comparison.calculatedValue, 0)} kWh</strong>
                                                </ResultsModalComparisonValue>
                                                <ResultsModalComparisonValue>
                                                    <span>Annual saving</span>
                                                    <strong>{currencySymbol}{round(comparison.costSaving, 2)}</strong>
                                                </ResultsModalComparisonValue>
                                                <ResultsModalComparisonValue>
                                                    <span>Simple payback</span>
                                                    <strong>{comparisonSimplePayback != null ? `${comparisonSimplePayback} yrs` : '—'}</strong>
                                                </ResultsModalComparisonValue>
                                                <ResultsModalComparisonValue>
                                                    <span>Break-even</span>
                                                    <strong>{comparisonBreakEvenYear != null ? `Year ${comparisonBreakEvenYear}` : 'Not within 25 years'}</strong>
                                                </ResultsModalComparisonValue>
                                            </>
                                        ) : (
                                            <span>{isLoadingPanelComparisons ? 'Calculating...' : 'Unavailable'}</span>
                                        )}
                                        {isSelected && <ResultsModalSelectedLabel>Selected for quote</ResultsModalSelectedLabel>}
                                    </ResultsModalComparisonCard>
                                );
                            })}
                        </ResultsModalComparisonGrid>
                        {panelComparisonError && <Alert variant='warning' className='mt-2 mb-0'>{panelComparisonError}</Alert>}
                    </ResultsModalSection>
                )}

                <ResultsModalDetailsSection>
                    <ResultsModalDetailsHeading>Estimate details</ResultsModalDetailsHeading>
                    <ResultsModalDetailsGrid>
                        <ResultRow label="Energy generated" value={`${round(calculatedValue, 0)} kWh / year`} />
                        <ResultRow label="Energy used in the home" value={`${round(usedEnergyValue, 0)} kWh / year`} />
                        <ResultRow label="Energy exported to grid" value={`${round(exportedEnergyValue, 0)} kWh / year`} />
                        <ResultRow label="Saving from used energy" value={`${currencySymbol}${round(usageIncome, 2)}`} />
                        <ResultRow label="Saving from exported energy" value={`${currencySymbol}${round(exportIncome, 2)}`} />
                        <ResultRow label="Total annual saving" value={`${currencySymbol}${round(costSaving, 2)}`} />
                        <ResultRow label="Installation cost" value={`${currencySymbol}${round(installationCost, 2)}`} />
                        <ResultRow label="Annual household electricity use" value={`${round(displayedAnnualConsumption, 0)} kWh / year`} />
                        <ResultRow label="Estimated self-consumption" value={`${round(displayedSelfConsumption, 0)}%`} />
                        <ResultRow label="Simple payback" value={displayedSimplePayback != null ? `${displayedSimplePayback} years` : 'Not available'} />
                        <ResultRow label="Break-even with degradation" value={displayedBreakEvenYear != null ? `Year ${displayedBreakEvenYear}` : 'Not within 25 years'} />
                        <ResultRow label="Annual CO2 saving" value={co2Saving != null ? `${co2Saving} kg` : '—'} />
                    </ResultsModalDetailsGrid>
                    <ResultsModalDetailsNote>
                        Self-consumption estimate uses annual household usage, generation and selected battery capacity.
                    </ResultsModalDetailsNote>
                </ResultsModalDetailsSection>

                <ResultsModalContactForm
                    onSubmit={formik.handleSubmit}
                    onPointerDownCapture={markJourneyInteraction}
                    onChangeCapture={(event) => noteJourneyField(event.target.name)}
                >
                    <ResultsModalFormBlock>
                        <ResultsModalFormLegend>1. Confirm your property address</ResultsModalFormLegend>
                        <ResultsModalFormHint>Use the postcode lookup, then check or amend the address.</ResultsModalFormHint>
                        <Row className='align-items-end'>
                            <Col xs={7} md={4} lg={3}>
                                <Form.Group>
                                    <Form.Label>Postcode</Form.Label>
                                    <Form.Control type='text' name='postcode' value={postCode} disabled />
                                </Form.Group>
                            </Col>
                            <Col xs={5} md={3} lg={2}>
                                <AddressButton
                                    type='button'
                                    variant='info'
                                    onClick={lookupAddresses}
                                    disabled={isAddressLoading}
                                >
                                    {isLookingUp ? <LoadingSpinner size='sm' /> : 'Find address'}
                                </AddressButton>
                            </Col>
                            {addressOptions.length > 0 && (
                                <Col xs={12} md={5} lg={7}>
                                    <Form.Group>
                                        <Form.Label>Select your address</Form.Label>
                                        <Form.Select onChange={(e) => setSelectedAddressId(e.target.value)} defaultValue="" disabled={isAddressLoading}>
                                            <option value="" disabled>Choose</option>
                                            {addressOptions.map(item => (
                                                <option key={item.id} value={item.id}>{item.address}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                        {isFetchingAddress && (
                            <Alert variant='info' className='mt-3 mb-0'>Loading selected address details...</Alert>
                        )}
                        {addressError && (
                            <Alert variant='danger' className='mt-3 mb-0'>{addressError}</Alert>
                        )}
                        <Row>
                            <Col xs={12} lg={6}>
                                <Form.Group>
                                    <Form.Label>Number and street</Form.Label>
                                    <Form.Control
                                        type='text'
                                        name='address1'
                                        value={formik.values.address1}
                                        onChange={formik.handleChange}
                                        disabled={isAddressLoading}
                                        isInvalid={!!formik.errors.address1}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formik.errors.address1}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label>Town</Form.Label>
                                    <Form.Control
                                        type='text'
                                        name='address2'
                                        value={formik.values.address2}
                                        onChange={formik.handleChange}
                                        disabled={isAddressLoading}
                                        isInvalid={!!formik.errors.address2}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formik.errors.address2}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label>County</Form.Label>
                                    <Form.Control
                                        type='text'
                                        name='address3'
                                        value={formik.values.address3}
                                        onChange={formik.handleChange}
                                        disabled={isAddressLoading}
                                        isInvalid={!!formik.errors.address3}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formik.errors.address3}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </ResultsModalFormBlock>

                    <ResultsModalFormBlock>
                        <ResultsModalFormLegend>2. Add your contact details</ResultsModalFormLegend>
                        <ResultsModalFormHint>These details are used to send and follow up your quote.</ResultsModalFormHint>
                        <Row>
                            <Col xs={6} lg={3}>
                                <Form.Group>
                                    <Form.Label>First name</Form.Label>
                                    <Form.Control
                                        type='text'
                                        name='firstName'
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        isValid={formik.touched.firstName && !formik.errors.firstName}
                                        isInvalid={!!formik.errors.firstName}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formik.errors.firstName}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={6} lg={3}>
                                <Form.Group>
                                    <Form.Label>Last name</Form.Label>
                                    <Form.Control
                                        type='text'
                                        name='lastName'
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        isValid={formik.touched.lastName && !formik.errors.lastName}
                                        isInvalid={!!formik.errors.lastName}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formik.errors.lastName}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={6} lg={3}>
                                <Form.Group>
                                    <Form.Label>Email address</Form.Label>
                                    <Form.Control
                                        type='email'
                                        name='email'
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        isValid={formik.touched.email && !formik.errors.email}
                                        isInvalid={!!formik.errors.email}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={6} lg={3}>
                                <Form.Group>
                                    <Form.Label>Phone number</Form.Label>
                                    <Form.Control
                                        type='tel'
                                        name='phone'
                                        value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        isValid={formik.touched.phone && !formik.errors.phone}
                                        isInvalid={!!formik.errors.phone}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formik.errors.phone}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </ResultsModalFormBlock>

                    <ResultsModalFormActions>
                        <PdfButton variant='warning' type='submit' disabled={isLoading}>
                            {isSending ? (
                                <>
                                    <LoadingSpinner size='sm' />
                                    {' '}Sending...
                                </>
                            ) : (
                                'Request a free home energy survey'
                            )}
                        </PdfButton>
                        <ResultsModalPrivacyNote>
                            By requesting a free home energy survey, you agree to your quote and details being sent to us. We will contact you to arrange the survey.
                        </ResultsModalPrivacyNote>
                        {sendResponse && (
                            <Alert variant={sendResponse.variant}>{sendResponse.text}</Alert>
                        )}
                    </ResultsModalFormActions>
                </ResultsModalContactForm>
            </ResultsModalBody>
            <Modal.Footer>
                <ModalButton onClick={closeResultsModal}>Close</ModalButton>
            </Modal.Footer>
        </ResultsModalContainer>
    );
};

export default ResultsModal;
